// Import necessary components
import React from "react";
import { format } from "date-fns";
import {
  recruitStatus,
  getStatusColor,
  getStatusTextColor,
} from "../components/Dropdowns/StatusDropdown";
import StarRating from "../components/StartRating";
import EvaluationSystemComponent from "../components/EvaluationSystemComponent";
import SimpleColumnHeader from "../components/SimpleColumnHeader";
import DynamicColumnHeader from "../components/DynamicColumnHeader";

// Font styling utility
export const getColumnFontClass = (columnId) => {
  // Group 1: Roboto font columns
  if (
    [
      "status",
      "code",
      "first_name",
      "last_name",
      "name",
      "second_name",
      "date_of_interview",
      "date_of_fired",
      "NDA_date",
      "FB_IG",
      "linkedin",
      "working_phone",
      "date_of_translate",
      "date_of_offer",
      "commentary",
    ].includes(columnId)
  ) {
    return "font-roboto text-xs";
  }

  // Group 2: Gazer Bold font columns
  if (
    [
      "rating",
      "company",
      "team",
      "department",
      "position",
      "division",
    ].includes(columnId)
  ) {
    return "font-gazer text-xs text-[#2B3674]";
  }

  // Group 3: Dynamic P/F columns
  if (columnId.startsWith("P") || columnId.startsWith("F")) {
    return "font-gazer-dynamic text-base";
  }

  // Default
  return "font-roboto text-xs";
};

// Utility for empty cell content
export const renderCellContent = (value, columnKey) => {
  // Check if the value is empty
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "string" && value.trim() === "")
  ) {
    // Return "Пусто" with the specified color
    return <span id="empty">(Пусто)</span>;
  }

  // For non-empty values, return the actual value
  return value;
};

// Utility for date formatting
export const formatDate = (date) => {
  if (!date) return null;
  try {
    return format(new Date(date), "dd.MM.yyyy");
  } catch (e) {
    console.warn("Invalid date:", date);
    return null;
  }
};

// Utility for encrypting values
export const encryptValue = () => {
  return (
    <div className="flex items-center justify-center gap-1">
      {[...Array(5)].map((_, i) => (
        <svg
          key={i}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 6V10M8 6L10.6667 3.33333M8 6L5.33333 3.33333M8 6V2M8 10L10.6667 12.6667M8 10L5.33333 12.6667M8 10V14M2 8H6M6 8L3.33333 5.33333M6 8L3.33333 10.6667M6 8H10M14 8H10M10 8L12.6667 5.33333M10 8L12.6667 10.6667"
            stroke="#6E6E6E"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ))}
    </div>
  );
};

// P/F Column headers
export const columnHeaders = {
  F12: (
    <div className="flex justify-between items-center text-black">
      <p>F12</p>
      <p>F12</p>
    </div>
  ),
  // "P" Columns
  P01_0: (
    <div className="flex justify-between items-center text-black">
      <p>P Персональная</p>
      <p>01-0</p>
    </div>
  ),
  P01_1: (
    <div className="flex justify-between items-center text-black">
      <p>Р Коллективная</p>
      <p>01-1</p>
    </div>
  ),
  P01_2: (
    <div className="flex justify-between items-center text-black">
      <p>Р Аналитическая</p>
      <p>01-2</p>
    </div>
  ),
  P01_3: (
    <div className="flex justify-between items-center text-black">
      <p>А Коллективная</p>
      <p>01-3</p>
    </div>
  ),
  P01_4: (
    <div className="flex justify-between items-center text-black">
      <p>А Персональная</p>
      <p>01-4</p>
    </div>
  ),
  F11: (
    <div className="flex justify-between items-center text-black">
      <p>F11</p>
      <p>F11</p>
    </div>
  ),
  P02_0: (
    <div className="flex justify-between items-center text-black">
      <p>Процес</p>
      <p>02-0</p>
    </div>
  ),
  P02_1: (
    <div className="flex justify-between items-center text-black">
      <p>Результат</p>
      <p>02-1</p>
    </div>
  ),
  P03_0: (
    <div className="flex justify-between items-center text-black">
      <p>Процедуры</p>
      <p>03-0</p>
    </div>
  ),
  P03_1: (
    <div className="flex justify-between items-center text-black">
      <p>Возможности</p>
      <p>03-1</p>
    </div>
  ),
  P04_0: (
    <div className="flex justify-between items-center text-black">
      <p>Содержание</p>
      <p>04-0</p>
    </div>
  ),
  P04_1: (
    <div className="flex justify-between items-center text-black">
      <p>Окружение</p>
      <p>04-1</p>
    </div>
  ),
  P05_0: (
    <div className="flex justify-between items-center text-black">
      <p>Внешняя</p>
      <p>05-0</p>
    </div>
  ),
  P05_1: (
    <div className="flex justify-between items-center text-black">
      <p>Внутренняя</p>
      <p>05-1</p>
    </div>
  ),
  P06_0: (
    <div className="flex justify-between items-center text-black">
      <p>Избегание</p>
      <p>06-0</p>
    </div>
  ),
  P06_1: (
    <div className="flex justify-between items-center text-black">
      <p>Стремление</p>
      <p>06-1</p>
    </div>
  ),
  P07_0: (
    <div className="flex justify-between items-center text-black">
      <p>Сам</p>
      <p>07-0</p>
    </div>
  ),
  P07_1: (
    <div className="flex justify-between items-center text-black">
      <p>Командный</p>
      <p>07-1</p>
    </div>
  ),
  P07_2: (
    <div className="flex justify-between items-center text-black">
      <p>Менеджер</p>
      <p>07-2</p>
    </div>
  ),
  P08_0: (
    <div className="flex justify-between items-center text-black">
      <p>Детальный</p>
      <p>08-0</p>
    </div>
  ),
  P08_1: (
    <div className="flex justify-between items-center text-black">
      <p>Глобальный</p>
      <p>08-1</p>
    </div>
  ),
  P09_0: (
    <div className="flex justify-between items-center text-black">
      <p>Различие</p>
      <p>09-0</p>
    </div>
  ),
  P09_1: (
    <div className="flex justify-between items-center text-black">
      <p>Сходство</p>
      <p>09-1</p>
    </div>
  ),
  P10_0: (
    <div className="flex justify-between items-center text-black">
      <p>Низкая</p>
      <p>10-0</p>
    </div>
  ),
  P10_1: (
    <div className="flex justify-between items-center text-black">
      <p>Высокая</p>
      <p>10-1</p>
    </div>
  ),
  P11_0: (
    <div className="flex justify-between items-center text-black">
      <p>Негатив</p>
      <p>11-0</p>
    </div>
  ),
  P11_1: (
    <div className="flex justify-between items-center text-black">
      <p>Позитив</p>
      <p>11-1</p>
    </div>
  ),

  // "F" Columns
  F1: (
    <div className="flex justify-between items-center text-black">
      <p>Жесткий</p>
      <p>F1</p>
    </div>
  ),
  F2: (
    <div className="flex justify-between items-center text-black">
      <p>Покладистость</p>
      <p>F2</p>
    </div>
  ),
  F3: (
    <div className="flex justify-between items-center text-black">
      <p>Консерватор</p>
      <p>F3</p>
    </div>
  ),
  F4: (
    <div className="flex justify-between items-center text-black">
      <p>Эволюционер</p>
      <p>F4</p>
    </div>
  ),
  F5: (
    <div className="flex justify-between items-center text-black">
      <p>Контролер</p>
      <p>F5</p>
    </div>
  ),
  F6: (
    <div className="flex justify-between items-center text-black">
      <p>Революционер</p>
      <p>F6</p>
    </div>
  ),
  F7: (
    <div className="flex justify-between items-center text-black">
      <p>Активный Автономный Сотрудник</p>
      <p>F7</p>
    </div>
  ),
  F8: (
    <div className="flex justify-between items-center text-black">
      <p>Активный Менеджер</p>
      <p>F8</p>
    </div>
  ),
  F9: (
    <div className="flex justify-between items-center text-black">
      <p>Общее Действие</p>
      <p>F9</p>
    </div>
  ),
  F10: (
    <div className="flex justify-between items-center text-black">
      <p>Активный</p>
      <p>F10</p>
    </div>
  ),
  F11: (
    <div className="flex justify-between items-center text-black">
      <p>F11</p>
      <p>F11</p>
    </div>
  ),
  F12: (
    <div className="flex justify-between items-center text-black">
      <p>F12</p>
      <p>F12</p>
    </div>
  ),
};

// Column metadata for display
export const columnMeta = {
  P01_0: { title: "P персоналльная", subtitle: "01-0" },
  P01_1: { title: "P Коллективная", subtitle: "01-1" },
  P01_2: { title: "P Аналитическая", subtitle: "01-2" },
  P01_3: { title: "А Коллективная", subtitle: "01-3" },
  P01_4: { title: "А Персональная", subtitle: "01-4" },
  P02_0: { title: "Процес", subtitle: "02-0" },
  P02_1: { title: "Результат", subtitle: "02-1" },
  P03_0: { title: "Процедуры", subtitle: "03-0" },
  P03_1: { title: "Возможности", subtitle: "03-1" },
  P04_0: { title: "Содержание", subtitle: "04-0" },
  P04_1: { title: "Окружение", subtitle: "04-1" },
  P05_0: { title: "Внешняя", subtitle: "05-0" },
  P05_1: { title: "Внутренняя", subtitle: "05-1" },
  P06_0: { title: "Избегание", subtitle: "06-0" },
  P06_1: { title: "Стремление", subtitle: "06-1" },
  P07_0: { title: "Сам", subtitle: "07-0" },
  P07_1: { title: "Командный", subtitle: "07-1" },
  P07_2: { title: "Менеджер", subtitle: "07-2" },
  P08_0: { title: "Детальный", subtitle: "08-0" },
  P08_1: { title: "Глобальный", subtitle: "08-1" },
  P09_0: { title: "Различие", subtitle: "09-0" },
  P09_1: { title: "Сходство", subtitle: "09-1" },
  P10_0: { title: "Низкая", subtitle: "10-0" },
  P10_1: { title: "Высокая", subtitle: "10-1" },
  P11_0: { title: "Негатив", subtitle: "11-0" },
  P11_1: { title: "Позитив", subtitle: "11-1" },
  F1: { title: "F1", subtitle: "Formula 1" },
  F2: { title: "F2", subtitle: "Formula 2" },
  F3: { title: "F3", subtitle: "Formula 3" },
  F4: { title: "F4", subtitle: "Formula 4" },
  F5: { title: "F5", subtitle: "Formula 5" },
  F6: { title: "F6", subtitle: "Formula 6" },
  F7: { title: "F7", subtitle: "Formula 7" },
  F8: { title: "F8", subtitle: "Formula 8" },
  F9: { title: "F9", subtitle: "Formula 9" },
  F10: { title: "F10", subtitle: "Formula 10" },
  F11: { title: "F11", subtitle: "Formula 11" },
  F12: { title: "F12", subtitle: "Formula 12" },
  rating: { title: "Rating", subtitle: "Candidate rating" },
  status: { title: "Status", subtitle: "Candidate status" },
  code: { title: "Code", subtitle: "Candidate code" },
  name: { title: "Name", subtitle: "Full name" },
  company: { title: "Company", subtitle: "Company name" },
  team: { title: "Team", subtitle: "Team name" },
  department: { title: "Department", subtitle: "Department name" },
  position: { title: "Position", subtitle: "Position title" },
  division: { title: "Division", subtitle: "Division name" },
  date_of_interview: { title: "Interview Date", subtitle: "Date of interview" },
  date_of_translate: {
    title: "Translate Date",
    subtitle: "Date of translation",
  },
  date_of_offer: { title: "Offer Date", subtitle: "Date of job offer" },
  date_of_fired: { title: "Termination Date", subtitle: "Date of termination" },
  commentary: { title: "Commentary", subtitle: "Additional notes" },
  working_phone: { title: "Work Phone", subtitle: "Work contact number" },
  NDA_date: { title: "NDA", subtitle: "NDA signed" },
  FB_IG: { title: "FB/IG", subtitle: "Social media" },
  linkedin: { title: "LinkedIn", subtitle: "LinkedIn profile" },
  output: { title: "Output", subtitle: "Calculated results" },
  motivation1: { title: "Motivation 1", subtitle: "Primary motivation" },
  motivation2: { title: "Motivation 2", subtitle: "Secondary motivation" },
  motivation3: { title: "Motivation 3", subtitle: "Tertiary motivation" },
  motivation4: { title: "Motivation 4", subtitle: "Quaternary motivation" },
};

// Define all possible columns
export const allTableColumns = {
  // Actions column
  actions: {
    id: "actions",
    header: "",
    cell: ({ row, startEditingRow }) => (
      <div className="group">
        <button
          type="button"
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          onClick={() => startEditingRow(row)}
        >
          <svg
            width="30"
            height="25"
            viewBox="0 0 30 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <mask id="path-1-inside-1_15730_118607" fill="white">
              <path d="M0 0H30V25H0V0Z" />
            </mask>
            <path d="M0 0H30V25H0V0Z" fill="white" />
            <path
              d="M30 24H0V26H30V24Z"
              fill="black"
              fillOpacity="0.05"
              mask="url(#path-1-inside-1_15730_118607)"
            />
            <path
              d="M9 18.5H21M9 18.5V15.4203L15 9.26098M9 18.5H12L18 12.3406M15 9.26098L17.1514 7.05234L17.1528 7.05103C17.4489 6.747 17.5972 6.59472 17.7682 6.53768C17.9189 6.48744 18.0811 6.48744 18.2318 6.53768C18.4027 6.59468 18.5509 6.74679 18.8466 7.05038L20.1515 8.38989C20.4485 8.69479 20.597 8.84731 20.6527 9.02311C20.7016 9.17774 20.7016 9.3443 20.6527 9.49894C20.5971 9.67461 20.4487 9.8269 20.1521 10.1314L20.1515 10.132L18 12.3406M15 9.26098L18 12.3406"
              stroke="#2B3674"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    ),
    size: 30,
    minSize: 30,
    enableResizing: false,
  },

  // Rating Column
  rating: {
    id: "rating",
    header: "Rating",
    cell: (value, { column }) => {
      const columnSize = column.getSize();
      const fontClass = getColumnFontClass("rating");

      // When column is active/narrow (small size)
      if (columnSize <= 50) {
        return (
          // This flex container centers the content within the entire cell
          <div className="flex items-center justify-center w-full h-full">
            {/* This relative container holds the SVG and the number */}
            <div className="relative flex items-center justify-center">
              {/* SVG background without the number */}
              <svg
                width="22"
                height="21"
                viewBox="0 0 22 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="0.5" width="21" height="21" rx="8" fill="#EBEFF2" />
              </svg>

              {/* 
                Dynamic value with bold font and exact color #2B3674
                Using font-bold for bold text and specific text color
              */}
              <span
                className="absolute text-center text-sm font-bold"
                style={{
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "#2B3674",
                }}
              >
                {value}
              </span>
            </div>
          </div>
        );
      }

      // Normal display with stars
      return (
        <div className={fontClass}>
          <StarRating rating={value} />
        </div>
      );
    },
    size: 190,
  },

  // Status Column
  status: {
    id: "status",
    header: "Status",
    cell: (value, { column }) => {
      const columnSize = column.getSize();
      const statusClass = getStatusColor(value);
      const statusClassText = getStatusTextColor(value);
      const fontClass = getColumnFontClass("status");

      return (
        <div className="flex items-center gap-2">
          <div
            className={`flex w-6 h-6 rounded-full justify-center ${statusClassText} items-center ${statusClass}`}
          >
            {value}
          </div>
          {columnSize >= 100 && (
            <span className={fontClass}>{recruitStatus[value]}</span>
          )}
        </div>
      );
    },
  },

  // Code Column
  code: {
    id: "code",
    header: "Code",
    cell: (value, { dataEncrypted }) => {
      const fontClass = getColumnFontClass("code");

      return (
        <div className={fontClass}>
          {dataEncrypted ? encryptValue(value) : value}
        </div>
      );
    },
  },

  // Name Column (derived from first_name and last_name)
  name: {
    id: "name",
    header: "Name",
    accessor: (row) => `${row.first_name} ${row.last_name}`,
    cell: (value) => <div className={getColumnFontClass("name")}>{value}</div>,
  },

  // First Name Column
  first_name: {
    id: "first_name",
    header: "First Name",
    cell: (value) => (
      <div className={getColumnFontClass("first_name")}>
        {renderCellContent(value, "first_name")}
      </div>
    ),
  },

  // Last Name Column
  last_name: {
    id: "last_name",
    header: "Last Name",
    cell: (value) => (
      <div className={getColumnFontClass("last_name")}>
        {renderCellContent(value, "last_name")}
      </div>
    ),
  },

  // Company Column
  company: {
    id: "company",
    header: "Компания",
    cell: (value) => (
      <div className={getColumnFontClass("company")}>
        {renderCellContent(value, "company")}
      </div>
    ),
  },

  // Team Column
  team: {
    id: "team",
    header: "Команда",
    cell: (value) => (
      <div className={getColumnFontClass("team")}>
        {renderCellContent(value, "team")}
      </div>
    ),
  },

  // Department Column
  department: {
    id: "department",
    header: "Department",
    cell: (value) => (
      <div className={getColumnFontClass("department")}>
        {renderCellContent(value, "department")}
      </div>
    ),
  },

  // Division Column
  division: {
    id: "division",
    header: "Division",
    cell: (value) => (
      <div className={getColumnFontClass("division")}>
        {renderCellContent(value, "division")}
      </div>
    ),
  },

  // Position Column
  position: {
    id: "position",
    header: "Position",
    cell: (value) => (
      <div className={getColumnFontClass("position")}>
        {renderCellContent(value, "position")}
      </div>
    ),
  },

  // Date of Interview Column
  date_of_interview: {
    id: "date_of_interview",
    header: "Date of Interview",
    cell: (value) => (
      <div className={getColumnFontClass("date_of_interview")}>
        {renderCellContent(formatDate(value), "date_of_interview")}
      </div>
    ),
  },

  // Date of Translate Column
  date_of_translate: {
    id: "date_of_translate",
    header: "Date of Translate",
    cell: (value) => (
      <div className={getColumnFontClass("date_of_translate")}>
        {renderCellContent(formatDate(value), "date_of_translate")}
      </div>
    ),
  },

  // Commentary Column
  commentary: {
    id: "commentary",
    header: "Commentary",
    cell: (value) => (
      <div className={getColumnFontClass("commentary")}>
        {renderCellContent(value, "commentary")}
      </div>
    ),
  },

  // Date of Offer Column
  date_of_offer: {
    id: "date_of_offer",
    header: "Date of Offer",
    cell: (value) => (
      <div className={getColumnFontClass("date_of_offer")}>
        {renderCellContent(formatDate(value), "date_of_offer")}
      </div>
    ),
  },

  // Date of Fired Column
  date_of_fired: {
    id: "date_of_fired",
    header: "Date of Fired",
    cell: (value) => (
      <div className={getColumnFontClass("date_of_fired")}>
        {renderCellContent(formatDate(value), "date_of_fired")}
      </div>
    ),
  },

  // Working Phone Column
  working_phone: {
    id: "working_phone",
    header: "Working Phone",
    cell: (value) => (
      <div className={getColumnFontClass("working_phone")}>
        {renderCellContent(value, "working_phone")}
      </div>
    ),
  },

  // NDA Column
  NDA_date: {
    id: "NDA_date",
    header: "NDA",
    cell: (value) => (
      <div className={getColumnFontClass("NDA_date")}>
        {renderCellContent(value, "NDA_date")}
      </div>
    ),
  },

  // FB/IG Column
  FB_IG: {
    id: "FB_IG",
    header: "FB/IG",
    cell: (value) => (
      <div className={getColumnFontClass("FB_IG")}>
        {renderCellContent(value, "FB_IG")}
      </div>
    ),
  },

  // LinkedIn Column
  linkedin: {
    id: "linkedin",
    header: "LinkedIn",
    cell: (value) => (
      <div className={getColumnFontClass("linkedin")}>
        <a
          href={value}
          className="flex items-center space-x-2 hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="24" height="24" rx="8" fill="#EBEFF2" />
            <path
              d="M11.3327 6.66605H6.66602V15.9994C6.66602 16.7358 7.26297 17.3327 7.99935 17.3327H15.9993C16.7357 17.3327 17.3327 16.7358 17.3327 15.9994V12.666M9.99935 13.9993L17.3327 6.66602M17.3327 6.66602H13.9993M17.3327 6.66602L17.3327 9.99935"
              stroke="#101216"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>LinkedIn</span>
        </a>
      </div>
    ),
  },

  // Phone Number Column
  phone_number: {
    id: "phone_number",
    header: "Phone Number",
    cell: (value) => (
      <div className={getColumnFontClass("phone_number")}>
        {renderCellContent(value, "phone_number")}
      </div>
    ),
  },

  // Email Column
  email: {
    id: "email",
    header: "Email",
    cell: (value) => (
      <div className={getColumnFontClass("email")}>
        {renderCellContent(value, "email")}
      </div>
    ),
  },

  // Motivation Columns
  motivation1: {
    id: "motivation1",
    header: "Motivation 1",
    cell: (value) => (
      <div className={getColumnFontClass("motivation1")}>
        {renderCellContent(value, "motivation1")}
      </div>
    ),
  },

  motivation2: {
    id: "motivation2",
    header: "Motivation 2",
    cell: (value) => (
      <div className={getColumnFontClass("motivation2")}>
        {renderCellContent(value, "motivation2")}
      </div>
    ),
  },

  motivation3: {
    id: "motivation3",
    header: "Motivation 3",
    cell: (value) => (
      <div className={getColumnFontClass("motivation3")}>
        {renderCellContent(value, "motivation3")}
      </div>
    ),
  },

  motivation4: {
    id: "motivation4",
    header: "Motivation 4",
    cell: (value) => (
      <div className={getColumnFontClass("motivation4")}>
        {renderCellContent(value, "motivation4")}
      </div>
    ),
  },

  // Output/Evaluation Column
  output: {
    id: "output",
    header: ({ upperValue, lowerValue, setUpperValue, setLowerValue }) => (
      <div>
        <EvaluationSystemComponent
          upperValue={upperValue}
          lowerValue={lowerValue}
          setUpperValue={setUpperValue}
          setLowerValue={setLowerValue}
        />
      </div>
    ),
    cell: ({ row, column, columnConditions, upperValue, lowerValue }) => {
      const columnWidth = column.getSize();
      const rowData = row.original;
      let yellowCount = 0;
      let blueCount = 0;
      let totalOutOfRangeCount = 0;

      // Count values based on color options
      Object.keys(rowData).forEach((key) => {
        if (
          (key.startsWith("P") || key.startsWith("F")) &&
          columnConditions[key]
        ) {
          const value = rowData[key];
          const conditions = columnConditions[key];

          // Skip if the condition bounds are empty
          if (
            conditions.lower?.value === "" ||
            conditions.upper?.value === ""
          ) {
            return;
          }

          // Determine if value falls outside the defined range
          const isInRange =
            (conditions.lower?.condition === "<"
              ? value >= conditions.lower?.value
              : value <= conditions.lower?.value) &&
            (conditions.upper?.condition === ">"
              ? value <= conditions.upper?.value
              : value >= conditions.upper?.value);

          // ONLY count in specific color counters if the column is explicitly marked
          if (conditions.colorOption === "blue" && isInRange) {
            blueCount++;
          } else if (conditions.colorOption === "yellow") {
            yellowCount++;
          }

          // Only count out-of-range cells if they're not in range
          if (!isInRange) {
            // Always count in the total
            totalOutOfRangeCount++;
          }
        }
      });

      // Return cell content with the color
      return (
        <div className="flex justify-center items-center w-full h-full relative py-2">
          {/* Centered number */}
          <p className="font-gazer-dynamic ">{totalOutOfRangeCount}</p>

          {/* Only show the badges if width >= 75 and either yellow or blue count is greater than zero */}
          {columnWidth >= 100 && (yellowCount > 0 || blueCount > 0) && (
            <div className="absolute inset-y-0 right-0 flex flex-row items-center gap-1 mr-1">
              {/* Blue count badge */}
              {blueCount > 0 && (
                <div>
                  <svg
                    width="28"
                    height="16"
                    viewBox="0 0 28 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="28" height="16" rx="8" fill="#4F7AE2" />
                    <text
                      x="14"
                      y="12"
                      fill="white"
                      fontSize="10"
                      fontWeight="600"
                      textAnchor="middle"
                    >
                      {blueCount}x
                    </text>
                  </svg>
                </div>
              )}
              {/* Yellow count badge */}
              {yellowCount > 0 && (
                <div>
                  <svg
                    width="28"
                    height="16"
                    viewBox="0 0 28 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="28" height="16" rx="8" fill="#F1E323" />
                    <text
                      x="14"
                      y="12"
                      fill="black" // Black text for better contrast on yellow
                      fontSize="10"
                      fontWeight="600"
                      textAnchor="middle"
                    >
                      {yellowCount}x
                    </text>
                  </svg>
                </div>
              )}
            </div>
          )}
        </div>
      );
    },
  },
};

// Generate dynamic columns for P and F values
export const generateDynamicColumns = () => {
  const dynamicColumns = {};

  // P columns
  for (let i = 1; i <= 11; i++) {
    const baseId = `P${i.toString().padStart(2, "0")}`;

    // P columns with _0, _1, etc. suffixes
    for (let j = 0; j <= 4; j++) {
      const columnId = `${baseId}_${j}`;

      dynamicColumns[columnId] = {
        id: columnId,
        header: columnHeaders[columnId] || (
          <div
            className={`flex justify-between items-center ${getColumnFontClass(
              columnId
            )}`}
          >
            <p>{columnId}</p>
          </div>
        ),
        cell: (value) => {
          const fontClass = getColumnFontClass(columnId);
          return <div className={fontClass}>{value + "%"}</div>;
        },
        size: 200,
        isDynamic: true,
      };
    }
  }

  // F columns
  for (let i = 1; i <= 12; i++) {
    const columnId = `F${i}`;

    dynamicColumns[columnId] = {
      id: columnId,
      header: columnHeaders[columnId] || (
        <div
          className={`flex justify-between items-center ${getColumnFontClass(
            columnId
          )}`}
        >
          <p>{columnId}</p>
          <p>{columnId}</p>
        </div>
      ),
      cell: (value) => {
        const fontClass = getColumnFontClass(columnId);
        return <div className={fontClass}>{value + "%"}</div>;
      },
      size: 200,
      isDynamic: true,
    };
  }

  return dynamicColumns;
};

// Combine all columns
export const getAllColumns = () => {
  return {
    ...allTableColumns,
    ...generateDynamicColumns(),
  };
};

// Default columns for a basic table
export const defaultColumns = ["actions", "rating", "status", "code", "name"];

// Column groups for different table types
export const columnGroups = {
  basic: ["actions", "rating", "status", "code", "name"],

  personal: [
    "actions",
    "rating",
    "status",
    "code",
    "name",
    "working_phone",
    "linkedin",
    "FB_IG",
    "NDA_date",
  ],

  workflow: [
    "actions",
    "rating",
    "status",
    "code",
    "name",
    "date_of_interview",
    "date_of_translate",
    "date_of_offer",
    "date_of_fired",
  ],

  organizational: [
    "actions",
    "rating",
    "status",
    "code",
    "name",
    "company",
    "team",
    "department",
    "position",
  ],
};
